// React, React Router DOM y React Hook Form
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import type { LoginForm, LoginProps } from "./types";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";

// Librerias externas
import { useUserStore } from "../../store/userStore";
import { useTranslation } from "react-i18next";
import { setLoggingOut } from "../../utils/tokenUtils";
import Swal from "sweetalert2";
import api from "../../utils/api";
import { brand } from "../../config/theme";

// Componentes
import { Badge, Button, SwitchTrack } from "../../components/ui";
import type { ButtonProps } from "../../components/ui";

// import CustomAlert from "../../components/base/CustomAlert";

// Funciones
import { toggleSwitch, authenticateUser } from "./functions";
import { preloadLandingChunk } from "./postLoginWarmup";
import {
  signInOrRegisterWithGoogle,
  RegisterError,
} from "../register/functions";
import {
  needsCompanyStep,
  REGISTER_COMPANY_STEP,
  type GoogleAuthResult,
} from "../register/googleFlow";
import { resolveLandingRoute } from "../../config/appsConfig";
import { captureNextPath, isCarrierOnboardingNext, isCargoMarketNext, isDeviceLinkNext, registerPathWithNext } from "../../utils/nextPath";
// import { resetAlert } from "../../../helpers";

// Iconos
import BrandLogo from "../../components/base/BrandLogo/BrandLogo";
// import TdcIcon from "../../../assets/images/tdc_logo2.png";

// Context

// Estilos
import {
  ContainerLogin,
  ContainerForm,
  Form,
  ContainerLabelInput,
  Label,
  ContainerInput,
  Input,
  ContainerInputPassword,
  InputPassword,
  EyeButton,
  Errors,
  ContainerButtons,
  LinkButton,
  ContainerSwitch,
  Switch,
  Language,
  ComponentIcon,
} from "./styles";
import { useAlert } from "../../context/AlertContext";
import { useTheme } from "../../context/ThemeContext";
import type { ThemeMode } from "../../context/ThemeContext";

type ViewState = "chooser" | "credentials" | "forgot";

const THEME_OPTIONS = [
  { mode: "light", icon: "ri-sun-line", labelKey: "common:theme.light" },
  { mode: "dark", icon: "ri-moon-line", labelKey: "common:theme.dark" },
  { mode: "system", icon: "ri-computer-line", labelKey: "common:theme.system" },
] as const satisfies readonly {
  mode: ThemeMode;
  icon: string;
  labelKey: string;
}[];

interface ForgotPasswordForm {
  email: string;
  website?: string; // Campo honeypot - debe estar vacío
}

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5c-2 1.5-4.7 2.5-7.6 2.5-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5C41.6 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
);

function SubmitButton({
  children,
  ...buttonProps
}: { children: ReactNode } & ButtonProps) {
  return (
    <Button {...buttonProps} fullWidth shape="pill">
      {children}
    </Button>
  );
}

function AuthHeader({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <ComponentIcon>
        <BrandLogo
          variant="main"
          alt=""
          className="max-h-24 sm:max-h-28 w-auto object-contain"
        />
      </ComponentIcon>
      <Badge tone="primary" variant="soft" className="uppercase tracking-wider">
        {title}
      </Badge>
    </div>
  );
}

function PreferencesBar({ isEnglish, setIsEnglish }: LoginProps) {
  const { i18n } = useTranslation();
  const { t } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();

  return (
    <ContainerSwitch>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-0.5 rounded-full border border-line bg-surface p-0.5">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.mode}
              type="button"
              onClick={() => setTheme(option.mode)}
              title={t(option.labelKey)}
              aria-label={t(option.labelKey)}
              aria-pressed={themeMode === option.mode}
              /*
                44 px donde se toca, 28 donde hay ratón.

                Con 28 el botón mide menos que la yema de un dedo: la guía de
                Apple y el criterio 2.5.5 de WCAG piden 44, y estos tres van
                pegados con 2 px de separación, así que fallar el objetivo
                significa cambiarle el tema a alguien que quería otra cosa.
                No se agranda el área con un pseudo-elemento porque a esa
                distancia las áreas se montarían entre sí.

                La condición es el PUNTERO, no el ancho. Con `sm:` un tablet de
                768 px volvía a 28: es ancho, pero se toca igual. `pointer-fine`
                sólo alcanza al ratón, que es donde 28 basta.
              */
              className={`flex h-11 w-11 pointer-fine:h-7 pointer-fine:w-7 items-center justify-center rounded-full transition-colors ${
                themeMode === option.mode
                  ? "bg-primary-500 text-on-primary"
                  : "text-content-muted hover:bg-subtle hover:text-content"
              }`}
            >
              <i className={`${option.icon} text-sm`} aria-hidden="true" />
            </button>
          ))}
        </div>
        <span className="h-5 w-px bg-line" />
        <Switch>
          <input
            type="checkbox"
            value={isEnglish ? "true" : "false"}
            className="sr-only peer"
            onChange={() =>
              toggleSwitch(isEnglish ? "es" : "en", setIsEnglish, i18n)
            }
            checked={!isEnglish}
          />
          <SwitchTrack peer />
          <Language>{isEnglish ? "Eng" : "Esp"}</Language>
        </Switch>
      </div>
    </ContainerSwitch>
  );
}

function Login({ isEnglish, setIsEnglish }: LoginProps) {
  // Hooks
  const navigate = useNavigate();
  //   const dispatch = useDispatch();

  // i18n
  const { t } = useTranslation(["login", "common"]);

  const { loading } = useUserStore();

  // Estados Locales y Globales
  const { showAlert } = useAlert();
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<ViewState>("chooser");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  // A dónde volver tras entrar (CC-CONSOLE#1561): `?next=` validado y guardado.
  const [nextPath] = useState<string | null>(() =>
    captureNextPath(typeof window !== "undefined" ? window.location.search : ""),
  );
  const linkNotice = isDeviceLinkNext(nextPath) ? (
    <p
      role="note"
      data-testid="login-link-notice"
      className="rounded-lg border border-primary-500/50 bg-primary-500/10 px-3 py-2 text-center text-xs leading-snug text-content"
    >
      <i className="ri-link-m mr-1 text-primary-500" aria-hidden="true" />
      {t("linkDeviceLoginNotice")}
    </p>
  ) : isCarrierOnboardingNext(nextPath) ? (
    <p
      role="note"
      data-testid="login-carrier-notice"
      className="rounded-lg border border-primary-500/50 bg-primary-500/10 px-3 py-2 text-center text-xs leading-snug text-content"
    >
      <i className="ri-truck-line mr-1 text-primary-500" aria-hidden="true" />
      {t("carrierOnboardingLoginNotice")}
    </p>
  ) : isCargoMarketNext(nextPath) ? (
    <p
      role="note"
      data-testid="login-cargo-market-notice"
      className="rounded-lg border border-primary-500/50 bg-primary-500/10 px-3 py-2 text-center text-xs leading-snug text-content"
    >
      <i className="ri-box-3-line mr-1 text-primary-500" aria-hidden="true" />
      {t("cargoMarketLoginNotice")}
    </p>
  ) : null;

  // Resetear flag de logout al cargar la página de login
  useEffect(() => {
    setLoggingOut(false);
  }, []);

  // Mientras la persona escribe su contraseña, se baja en reposo el chunk de
  // la pantalla a la que llegará: al iniciar sesión ya está en el navegador.
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => void preloadLandingChunk());
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(() => void preloadLandingChunk(), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Contador regresivo para rate limiting
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(rateLimitCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitCountdown]);

  // React Hook Form para recuperación de contraseña
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors },
    reset: resetForgot,
  } = useForm<ForgotPasswordForm>({ mode: "onBlur" });

  // Función para solicitar recuperación de contraseña
  const onForgotSubmit: SubmitHandler<ForgotPasswordForm> = async (data) => {
    setForgotLoading(true);
    try {
      await api.post("/auth/request-password-reset", {
        email: data.email.toLowerCase(),
        website: data.website, // Campo honeypot
      });

      await Swal.fire({
        icon: "success",
        title: t("forgotPasswordSuccess"),
        text: t("forgotPasswordEmailSent"),
        confirmButtonText: t("common:general.accept"),
        confirmButtonColor: brand.primary,
      });

      resetForgot();
      setView("credentials");
    } catch (error: unknown) {
      // Verificar si es error de rate limiting (429)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 429) {
        // Iniciar contador de 5 minutos (300 segundos)
        setRateLimitCountdown(300);
        await Swal.fire({
          icon: "warning",
          title: t("common:general.warning"),
          text: t("rateLimitMessage"),
          confirmButtonText: t("common:general.accept"),
          confirmButtonColor: brand.primary,
        });
        setForgotLoading(false);
        return;
      }

      // Por seguridad, el backend siempre responde igual
      // pero mostramos mensaje de éxito de todos modos
      await Swal.fire({
        icon: "success",
        title: t("forgotPasswordSuccess"),
        text: t("forgotPasswordEmailSent"),
        confirmButtonText: t("common:general.accept"),
        confirmButtonColor: brand.primary,
      });

      resetForgot();
      setView("credentials");
    } finally {
      setForgotLoading(false);
    }
  };

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    // watch,
    formState: { errors },
  } = useForm<LoginForm>({ mode: "onBlur" });
  //   const watchAllFields = watch();

  // Función para enviar el formulario
  const onSubmit: SubmitHandler<LoginForm> = async (data) => {
    await authenticateUser(
      data.emailOrUsername,
      data.password,
      navigate,
      showAlert,
      t,
    );
  };

  // Iniciar sesión / registrarse con Google. Si el correo ya existe con una
  // cuenta creada manualmente, el flujo enlaza Google a esa cuenta sin pedir la
  // contraseña (ver signInOrRegisterWithGoogle).
  const onGoogle = async () => {
    setGoogleLoading(true);
    try {
      const result = (await signInOrRegisterWithGoogle()) as GoogleAuthResult;
      // Ya tenía cuenta con contraseña y Firebase se la quitó al vincular
      // Google: se le avisa antes de entrar.
      if (result.passwordRemoved) {
        await Swal.fire({
          icon: "warning",
          title: t("googlePasswordRemovedTitle"),
          text: t("googlePasswordRemovedText"),
          confirmButtonText: t("accept"),
          confirmButtonColor: brand.primary,
        });
      }
      // Cuenta recién creada: sigue al alta (empresa → licencia → pago) en vez
      // de caer en la plataforma sin empresa ni licencia.
      if (needsCompanyStep(result, "login")) {
        navigate(REGISTER_COMPANY_STEP.pathname, {
          replace: true,
          state: REGISTER_COMPANY_STEP.state,
        });
        return;
      }
      // Con `next` (vincular un dispositivo, CC-CONSOLE#1561) se vuelve ahí.
      if (nextPath) {
        const { resolveNextOr } = await import("../onboarding/onboardingService");
        navigate(await resolveNextOr(resolveLandingRoute()), { replace: true });
        return;
      }
      navigate(resolveLandingRoute());
    } catch (err) {
      const key = err instanceof RegisterError ? err.message : "errorGoogle";
      // No molestar si el usuario simplemente cerró el popup.
      if (key !== "errorPopupClosed") {
        showAlert({
          title: t("errorLogin"),
          text: t(key),
          icon: "error",
          confirmButtonText: t("accept"),
        });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Render del formulario de recuperación de contraseña
  const renderForgotPasswordForm = () => (
    <ContainerLogin>
      <AuthHeader title={t("recoverPassword")} />
      <ContainerForm>
        <Form onSubmit={handleSubmitForgot(onForgotSubmit)}>
          <p className="text-content-muted text-xs sm:text-sm text-center">
            {t("forgotPasswordDescription")}
          </p>
          {/* Campo Honeypot - Oculto para usuarios, visible para bots */}
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0,
              height: 0,
              overflow: "hidden",
            }}
            aria-hidden="true"
          >
            <label htmlFor="website">Website</label>
            <input
              {...registerForgot("website")}
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <ContainerLabelInput>
            <Label htmlFor="forgotEmail">{t("email")}:</Label>
            <ContainerInput>
              <Input
                {...registerForgot("email", {
                  required: "requiredField",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "invalidEmail",
                  },
                })}
                type="email"
                id="forgotEmail"
                name="email"
                placeholder={t("placeholderEmail")}
                styleprop={forgotErrors?.email}
              />
              {forgotErrors?.email && (
                <Errors>{t(forgotErrors?.email?.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerButtons>
            <SubmitButton
              type="submit"
              loading={forgotLoading || rateLimitCountdown > 0}
            >
              {forgotLoading
                ? t("common:general.loading")
                : rateLimitCountdown > 0
                  ? t("waitMinutes", {
                      minutes: Math.ceil(rateLimitCountdown / 60),
                    })
                  : t("sendRecoveryEmail")}
            </SubmitButton>
            <LinkButton type="button" onClick={() => setView("credentials")}>
              <i className="ri-arrow-left-line mr-2" />
              {t("backToLogin")}
            </LinkButton>
          </ContainerButtons>
        </Form>
      </ContainerForm>
      <PreferencesBar isEnglish={isEnglish} setIsEnglish={setIsEnglish} />
    </ContainerLogin>
  );

  // Render del selector de método de inicio de sesión (pantalla inicial).
  // Muestra dos botones (Google / usuario y contraseña) sin revelar los campos.
  const renderChooser = () => (
    <ContainerLogin>
      <AuthHeader title={t("welcome")} />
      <ContainerForm>
        {linkNotice}
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="secondary"
            shape="pill"
            fullWidth
            onClick={onGoogle}
            loading={googleLoading}
            disabled={loading}
            icon={<GoogleIcon />}
          >
            {t("loginWithGoogle")}
          </Button>

          <Button
            type="button"
            variant="secondary"
            shape="pill"
            fullWidth
            onClick={() => setView("credentials")}
            disabled={googleLoading || loading}
            icon={<i className="ri-mail-line text-lg" aria-hidden="true" />}
          >
            {t("loginWithPassword")}
          </Button>

          <Button
            type="button"
            variant="secondary"
            shape="pill"
            fullWidth
            onClick={() => navigate(registerPathWithNext(nextPath))}
            disabled={googleLoading || loading}
            icon={<i className="ri-user-add-line text-lg" aria-hidden="true" />}
          >
            {t("signUp")}
          </Button>
        </div>
      </ContainerForm>
      <PreferencesBar isEnglish={isEnglish} setIsEnglish={setIsEnglish} />
    </ContainerLogin>
  );

  // Render del formulario de usuario y contraseña
  const renderCredentialsForm = () => (
    <ContainerLogin>
      <AuthHeader title={t("welcome")} />
      <ContainerForm>
        {linkNotice}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ContainerLabelInput>
            <Label htmlFor="emailOrUsername">{t("emailOrUsername")}:</Label>
            <ContainerInput>
              <Input
                {...register("emailOrUsername", {
                  required: "requiredField",
                  minLength: {
                    value: 3,
                    message: "minimumCharacters",
                  },
                })}
                type="text"
                id="emailOrUsername"
                name="emailOrUsername"
                placeholder={t("placeholderEmailOrUsername")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue("emailOrUsername", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
                styleprop={errors?.emailOrUsername}
              />
              {errors?.emailOrUsername && (
                <Errors>{t(errors?.emailOrUsername?.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerLabelInput>
            <Label htmlFor="password">{t("password")}:</Label>
            <ContainerInput>
              <ContainerInputPassword styleprop={errors?.password}>
                <InputPassword
                  {...register("password", {
                    required: "requiredField",
                    minLength: {
                      value: 6,
                      message: "minimumCharacters",
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder={t("placeholderPassword")}
                />
                <EyeButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i
                    className={`${showPassword ? "ri-eye-line" : "ri-eye-off-line"} text-lg text-content-muted`}
                  />
                </EyeButton>
              </ContainerInputPassword>
              {errors?.password && (
                <Errors>{t(errors.password.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerButtons>
            <SubmitButton type="submit" loading={loading}>
              {loading ? t("common:general.loading") : t("login")}
            </SubmitButton>
            <div className="flex w-full items-center justify-between gap-2">
              <LinkButton type="button" onClick={() => setView("chooser")}>
                <i className="ri-arrow-left-line mr-1" />
                {t("backToLogin")}
              </LinkButton>
              <LinkButton type="button" onClick={() => setView("forgot")}>
                {t("forgotPassword")}
              </LinkButton>
            </div>
          </ContainerButtons>

          <div className="flex flex-col items-center gap-2 mt-4 text-xs">
            <span className="text-content-muted">{t("dontHaveAccount")}</span>
            <LinkButton type="button" onClick={() => navigate(registerPathWithNext(nextPath))}>
              <i className="ri-user-add-line mr-1" />
              {t("createAccountNow")}
            </LinkButton>
          </div>
        </Form>
      </ContainerForm>
      <PreferencesBar isEnglish={isEnglish} setIsEnglish={setIsEnglish} />
    </ContainerLogin>
  );

  if (view === "forgot") return renderForgotPasswordForm();
  if (view === "credentials") return renderCredentialsForm();
  return renderChooser();
}

export default Login;
